import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { partnerGroupChannelQueryKeys } from '../api/use-partner-group-channel-query';
import { buildPartnerGroupChannelMessageMapOptions } from '../components/Partner/Community/mapPartnerGroupChannel';
import { SOCKET_EVENTS } from '../constants/socketEvents';
import { useAuth } from '../contexts/AuthContext';
import { useNotificationContext } from '../contexts/NotificationProvider';
import { useSocket } from '../contexts/SocketProvider';
import { handleForegroundPushMessage } from '../services/fcmPushDisplay';
import { hydrateMediaUrls } from '../utils/mediaSas';
import { useSocketEvent } from './useSocketEvent';
import {
  getPartnerGroupChatSenderUid,
  getPartnerGroupChatSocketChannelId,
  mapPartnerGroupChatSocketToChatMessage,
  mapPartnerGroupChatSocketToPushPayload,
} from '../utils/partnerGroupChatSocketPayload';
import {
  appendPartnerGroupChatMessageToCache,
  applySenderIdentityFromChannelCache,
  PARTNER_GROUP_CHANNEL_MESSAGES_PAGE_SIZE,
} from '../components/Partner/Community/partnerGroupChannelMessagesCache';
import {
  doesSocketChannelMatchLiveCommunityView,
  getPartnerCommunityLiveView,
} from '../components/Partner/Community/partnerCommunityLiveView';

/**
 * Real-time partner community chat messages for partner org users.
 * Backend delivers to room user:{partnerDbUserId}, excluding the sender.
 * Reactions use message:reaction_* via useMessageReactionSocketListener (not partner:group_chat:reaction).
 */
export const usePartnerGroupChatSocketListener = ({ enabled = true } = {}) => {
  const queryClient = useQueryClient();
  const { isPartner, firebaseUid, partnerProfile, incrementNotificationCount } = useAuth();
  const { isConnected } = useSocket();
  const { openNotification } = useNotificationContext() ?? {};

  const messageMapOptions = useMemo(
    () => buildPartnerGroupChannelMessageMapOptions({ firebaseUid, partnerProfile }),
    [firebaseUid, partnerProfile],
  );

  const handlePartnerGroupChatMessage = useCallback(
    (envelope) => {
      if (!isPartner) return;

      const senderUid = getPartnerGroupChatSenderUid(envelope);
      if (senderUid && firebaseUid && senderUid === firebaseUid) {
        return;
      }

      // Socket payloads use bare blob URLs; append cached mediaSasToken from HTTP responses.
      hydrateMediaUrls(envelope);

      const socketChannelId = getPartnerGroupChatSocketChannelId(envelope);
      const mappedMessage = mapPartnerGroupChatSocketToChatMessage(envelope, messageMapOptions);
      const liveView = getPartnerCommunityLiveView();
      const cacheChannelId = liveView.streamChannelId || socketChannelId;
      const chatMessage = applySenderIdentityFromChannelCache(queryClient, mappedMessage, {
        limit: PARTNER_GROUP_CHANNEL_MESSAGES_PAGE_SIZE,
        channelId: cacheChannelId,
      });
      const isViewingSameCommunityChat =
        doesSocketChannelMatchLiveCommunityView(socketChannelId) && Boolean(chatMessage);

      if (isViewingSameCommunityChat) {
        appendPartnerGroupChatMessageToCache(queryClient, chatMessage, {
          limit: PARTNER_GROUP_CHANNEL_MESSAGES_PAGE_SIZE,
          channelId: cacheChannelId,
        });
        return;
      }

      queryClient.invalidateQueries({
        queryKey: [...partnerGroupChannelQueryKeys.all, 'messages'],
      });

      const pushPayload = mapPartnerGroupChatSocketToPushPayload(envelope);

      void handleForegroundPushMessage(pushPayload, {
        onOpen: openNotification,
        onNewNotification: incrementNotificationCount,
      });
    },
    [
      firebaseUid,
      incrementNotificationCount,
      isPartner,
      messageMapOptions,
      openNotification,
      queryClient,
    ],
  );

  useSocketEvent(
    SOCKET_EVENTS.PARTNER_GROUP_CHAT_MESSAGE,
    enabled && isPartner && isConnected ? handlePartnerGroupChatMessage : null,
    [enabled, isPartner, isConnected, handlePartnerGroupChatMessage],
  );
};

export default usePartnerGroupChatSocketListener;
